import { FolderPlus, Trash2 } from 'lucide-react';
import { ReasonButton } from '../../../../components/ui/ReasonButton';
import { STORAGE_READ_ONLY_REASON } from '../../../../core/engines/azureStorageRefusal';
import { useStorageExplorerStore } from '../store/storageExplorerStore';
import { registerStorageToolbarExtension, TOOLBAR_OVERFLOW_PRIORITY, type StorageToolbarContext } from '../toolbar/toolbarExtensions';
import { StorageToolbarMenuItem } from '../toolbar/StorageToolbarMenuItem';
import { StorageNewFolderDialog } from './StorageNewFolderDialog';
import { StorageUploadMenu, uploadDestination } from './StorageUploadMenu';
import { confirmStorageDelete } from './storageMutations';

const visibleInBlobLocation = (ctx: StorageToolbarContext) => ctx.mode === 'blobs' && uploadDestination(ctx) !== undefined;
/** S8: Upload / Delete also act in the Data Lake view (blob refs of its rows); this New folder (a virtual folder) stays Blob-only — the Data Lake view has its own New folder (a real directory). */
const visibleInBlobOrLakeLocation = (ctx: StorageToolbarContext) => (ctx.mode === 'blobs' || ctx.mode === 'hns') && uploadDestination(ctx) !== undefined;

/** New folder's action and its reason — shared by the button and its More-menu form (C1 R5 / N2 overflow). */
function useNewFolderAction(ctx: StorageToolbarContext): { reason?: string; open: () => void } {
  const destination = uploadDestination(ctx);
  const tabId = typeof ctx.capabilities.tabId === 'string' ? ctx.capabilities.tabId : undefined;
  // A9 / U12: unavailable with a reason a keyboard user reaches (never a native disabled button with a title only).
  const writeRefusal = typeof ctx.capabilities.writeRefusal === 'string' ? ctx.capabilities.writeRefusal : undefined;
  // A folder here only exists to upload into: a SAS that cannot upload cannot use it either.
  const uploadRefusal = typeof ctx.capabilities.uploadRefusal === 'string' ? ctx.capabilities.uploadRefusal : undefined;
  const reason = ctx.readOnly ? STORAGE_READ_ONLY_REASON : !destination ? 'Open a container or folder first' : writeRefusal ?? uploadRefusal;
  const open = () => {
    if (!destination || !tabId || reason) return;
    const parentPrefix = destination.kind === 'blobPrefix' ? destination.prefix : '';
    ctx.openDialog(<StorageNewFolderDialog
      open
      container={destination.container}
      parentPrefix={parentPrefix}
      onOpenChange={(next) => { if (!next) ctx.openDialog(null); }}
      onCreate={(folders) => { for (const folder of folders) useStorageExplorerStore.getState().addPendingFolder(tabId, folder); }}
    />);
  };
  return { reason, open };
}

function NewFolderButton({ ctx }: { ctx: StorageToolbarContext }) {
  const { reason, open } = useNewFolderAction(ctx);
  return <ReasonButton variant="outline" size="xs" className="gap-1" reason={reason} title="Add an empty upload target" onClick={open} data-testid="storage-toolbar-new-folder"><FolderPlus className="h-3.5 w-3.5" /> New folder</ReasonButton>;
}

function NewFolderMenuItem({ ctx }: { ctx: StorageToolbarContext }) {
  const { reason, open } = useNewFolderAction(ctx);
  return <StorageToolbarMenuItem icon={FolderPlus} label="New folder…" reason={reason} onSelect={open} testId="storage-toolbar-new-folder" />;
}

/** A More-menu entry (UI/UX fix round A, A4): a real menu item, last and after a separator (destructive). */
function DeleteMenuItem({ ctx }: { ctx: StorageToolbarContext }) {
  const writeRefusal = typeof ctx.capabilities.writeRefusal === 'string' ? ctx.capabilities.writeRefusal : undefined;
  const reason = ctx.readOnly ? STORAGE_READ_ONLY_REASON : writeRefusal ?? (ctx.selection.length === 0 ? 'Select an item to delete' : undefined);
  return <StorageToolbarMenuItem icon={Trash2} label="Delete…" shortcut="Del" destructive reason={reason} onSelect={() => void confirmStorageDelete({ connectionId: ctx.connectionId, resources: ctx.selection, source: 'toolbar', refresh: ctx.refresh })} testId="storage-toolbar-delete" />;
}

export function registerS2StorageToolbarExtensions(): () => void {
  const unregister = [
    registerStorageToolbarExtension({ id: 's2-upload', placement: 'primary', order: 100, isVisible: visibleInBlobOrLakeLocation, render: (ctx) => <StorageUploadMenu ctx={ctx} /> }),
    registerStorageToolbarExtension({
      id: 's2-new-folder', placement: 'primary', order: 110, isVisible: visibleInBlobLocation, render: (ctx) => <NewFolderButton ctx={ctx} />,
      overflow: { priority: TOOLBAR_OVERFLOW_PRIORITY.newFolder, renderMenuItem: (ctx) => <NewFolderMenuItem ctx={ctx} /> },
    }),
    registerStorageToolbarExtension({ id: 's2-delete', placement: 'more', destructive: true, order: 120, isVisible: visibleInBlobOrLakeLocation, render: (ctx) => <DeleteMenuItem ctx={ctx} /> }),
  ];
  return () => unregister.forEach((dispose) => dispose());
}
